import React, { useState, useEffect } from "react";
import { Route, Redirect } from "react-router-dom";
import { isAuthenticated } from "../utils/user";
import notify from "./notifier";

/**

 * A wrapper for Route that ensures users are authenticated and have the required privileges
 * @param {Object} props - Component props
 * @param {React.Component} props.component - Component to render if authenticated and authorized
 * @param {Array} props.requiredPrivileges - Array of privilege names required to access the route
 * @param {Object} props.rest - Additional props to pass to the Route component
 */
export const ProtectedRoute = ({ 
  component: Component, 
  requiredPrivileges = [],
  ...rest 
}) => {
  const [authStatus, setAuthStatus] = useState(null); // `null` means loading

  useEffect(() => {
    // Skip auth check for e-sign routes
    const path = rest.path || (rest.location && rest.location.pathname);
    if (path && (path.startsWith('/e-sign/sign/') || path === '/e-sign/complete')) {
      setAuthStatus(true);
      return;
    }

    const checkAuth = async () => {
      const val = await isAuthenticated();
      setAuthStatus(val);
    };
    checkAuth();
  }, [rest.path, rest.location]);

  /**
   * Check if the user has the required privileges
   * @param {Array} privileges - Array of required privilege names
   * @returns {boolean} - True if user has any of the required privileges or no privileges are required
   */
  const hasRequiredPrivileges = (privileges) => {
    // If this is an e-sign route, no privileges needed
    const path = rest.path || (rest.location && rest.location.pathname);
    if (path && (path.startsWith('/e-sign/sign/') || path === '/e-sign/complete')) {
      return true;
    }

    // If no privileges are required, allow access
    if (!privileges || privileges.length === 0) {
      return true;
    }

    try {
      // Get stored privileges from localStorage
      const userPrivileges = JSON.parse(localStorage.getItem('document_service_privileges') || '[]');
      
      // Check if user has any of the required privileges
      return Array.isArray(userPrivileges) && 
             privileges.some(privilege => userPrivileges.includes(privilege));
    } catch (error) {
      console.error('Error checking privileges:', error);
      return false;
    }
  };

  if (authStatus === null) {
    // Show a loading indicator while checking authentication
    return <div>Loading...</div>;
  }

  return (
    <Route
      {...rest}
      render={props => {
        if (authStatus) {
          // User is authenticated, now check privileges
          if (hasRequiredPrivileges(requiredPrivileges)) {
            // User has required privileges, render the component
            return <Component {...props} {...rest} />;
          } else {
            // User lacks required privileges, notify and redirect
            notify({
              type: "error",
              message: "You don't have permission to access this page.",
              autoClose: 7000,
              limit: 1
            });
            
            // Redirect to home or an unauthorized page
            return (
              <Redirect
                to={{
                  pathname: "/unauthorized", // or "/" if you don't have an unauthorized page
                  state: { 
                    from: props.location,
                    requiredPrivileges: requiredPrivileges 
                  }
                }}
              />
            );
          }
        } else {
          // User is not authenticated, notify and redirect to login
          notify({
            type: "warn",
            message: "Session timed out, please login again.",
            autoClose: 7000,
            limit: 1
          });
          
          setTimeout(() => {
            window.location.href = "/login";
          }, 1000);
          
          return (
            <Redirect
              to={{
                pathname: "/login",
                state: { from: props.location }
              }}
            />
          );
        }
      }}
    />
  );
};